import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import { Observe } from 'rewire-core';
import { WithStyle, Sortable, SortableList, IActionMenuItem, IItem } from 'rewire-ui';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ArchiveIcon from '@material-ui/icons/ArchiveOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import DuplicateIcon from '@material-ui/icons/FileCopyOutlined';
// import UnarchiveIcon            from '@material-ui/icons/UnarchiveOutlined';
import { CalendarMonth as CalendarMonthIcon } from 'mdi-material-ui';
import { IArea, IGroup, IView } from '../DataTypes';
import mainPageModel from '../main-navigation/MainPageModel';
import { RouteManagerBase } from '../main-navigation/RouteManager';
import Routes from '../Routes';
import NavLink from '../ui/NavLink';
import OptionsActionMenu from '../ui/OptionsActionMenu';
import AdvancedSearchDialog from './AdvancedSearchDialog';
import AddAreaDialog from './AddAreaDialog';
import AddGroupDialog from './AddGroupDialog';
import RenameAreaDialog from './RenameAreaDialog';
import RenameGroupDialog from './RenameGroupDialog';
import DuplicateAreaDialog from './DuplicateAreaDialog';
import DuplicateGroupDialog from './DuplicateGroupDialog';
import ArchiveDeleteAreaDialog from '../settings/ArchiveDeleteAreaDialog';
import ArchiveDeleteGroupDialog from '../settings/ArchiveDeleteGroupDialog';
import SearchViewDialog from './SearchViewDialog';
import mainDateRangeNavModel from '../ui/MainDateRangeNavModel';
import sidePanelContentsModel from './SidePanelContentsModel';
import sideTogglePanelModel from './SideTogglePanelModel';
import { withStyles } from '../utility/styles';

const sortableListRenderer = (props: any) => {
  return <List style={props.style}>{props.children}</List>;
};

const viewsStyles = (_theme: Theme) => ({
  viewsSummaryContent: {
    display: 'block',
    flexGrow: 0,
    width: '100%',
  },
  viewsDetails: {
    paddingTop: '0px',
    paddingBottom: '5px',
  },
  viewsSecondaryAction: {
    top: 0,
    height: '100%',
    transform: 'none',
  },
  viewsNone: {
    paddingLeft: '30px',
  },
  sharedViewsOptions: {
    position: 'absolute',
    height: '100%',
    top: 0,
    right: 0,
  },
  listItemContainer: {
    '&:hover $listItemOptions': {
      transitionDelay: '35ms',
      transitionProperty: 'visibility',
    },
  },
  listItemLink: {},
  listItemLinkText: {},
  activeLink: {},
  listItemOptions: {},
  listItemOptionsVisible: {},
  listItemOptionsTypography: {},
  menuItem: {},
  actionButton: {},
  sortableListItemContainer: {},
});

interface IViewsProps {
  location: any;
  classes: any;
}

type ViewsProps = WithStyle<ReturnType<typeof viewsStyles>, IViewsProps>;

const Views = withStyles<IViewsProps>(
  viewsStyles,
  class extends React.Component<ViewsProps> {
    viewsOptionsActionMenuItems: IActionMenuItem[];

    constructor(props: ViewsProps) {
      super(props);

      this.viewsOptionsActionMenuItems = [
        {
          name: 'advancedSearch',
          title: 'Advanced Search',
          icon: CalendarMonthIcon,
          onClick: this.handleAdvancedSearchClick,
          closeOnClick: true,
          disabled: () => false,
        },
      ];
    }

    shouldComponentUpdate(nextProps: ViewsProps) {
      return nextProps.location !== this.props.location;
    }

    handleAdvancedSearchClick = () => {
      sidePanelContentsModel.showAdvancedSearchDialog();
    };

    handleEditSearchViewClick = (view: IView) => () => {
      sidePanelContentsModel.showSearchViewDialog(view);
    };

    handleDeleteSearchViewClick = (viewId: string) => () => {
      sidePanelContentsModel.deleteSearchView(viewId);
    };

    isActive = (viewId: string) => (match: any, location: any) => {
      const searchParams = new URLSearchParams(location.search);
      const urlSearchViewId = searchParams.get('viewId');
      return viewId === urlSearchViewId;
    };

    viewRenderer = (view: IView): JSX.Element => {
      const optionsActionMenuItems = [
        {
          name: 'editView',
          title: 'Edit View',
          icon: EditIcon,
          onClick: this.handleEditSearchViewClick(view),
          closeOnClick: true,
          disabled: () => !mainPageModel.canEditViews,
        },
        {
          name: 'deleteView',
          title: 'Delete View',
          icon: DeleteIcon,
          onClick: this.handleDeleteSearchViewClick(view.id),
          closeOnClick: true,
          disabled: () => !mainPageModel.canDeleteViews,
        },
      ];

      return (
        <Observe
          render={() => {
            const tabIndex = sidePanelContentsModel.viewsPanelIsExpanded ? 0 : -1;
            const viewLinkData = mainPageModel.viewMap[view.id].viewLinkData;
            const toUrl = RouteManagerBase.current.createLinkFor({ effective: mainDateRangeNavModel.effective, ...viewLinkData });
            const isActiveFn = this.isActive(view.id);
            const { classes } = this.props;

            return (
              <ListItem
                key={view.id}
                tabIndex={tabIndex}
                className={this.props.classes.listItemLink}
                classes={{ container: this.props.classes.listItemContainer }}
                component={NavLink}
                to={toUrl}
                activeClassName={classes.activeLink}
                isActive={isActiveFn}
              >
                <Typography variant="body2" className={classes.listItemLinkText}>
                  {view.name}
                </Typography>
                <ListItemSecondaryAction
                  className={classNames(
                    classes.listItemOptions,
                    isActiveFn(undefined, this.props.location) ? classes.listItemOptionsVisible : undefined,
                  )}
                >
                  <OptionsActionMenu
                    title="View Options"
                    items={optionsActionMenuItems}
                    classes={{
                      menuItem: classes.menuItem,
                      menuButton: classes.actionButton,
                      listItemTypography: classes.listItemOptionsTypography,
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            );
          }}
        />
      );
    };

    render() {
      const { classes } = this.props;

      return (
        <Observe
          render={() => (
            <Accordion onChange={(evt: object, expanded: boolean) => sidePanelContentsModel.setViewsPanelExpanded(expanded)}>
              <AccordionSummary classes={{ content: classes.viewsSummaryContent }}>
                <Typography variant="subtitle1">Shared Views</Typography>
                <div
                  className={classes.sharedViewsOptions}
                  role="menu"
                  onClick={(evt: React.MouseEvent<any>) => {
                    evt.stopPropagation();
                  }}
                >
                  <OptionsActionMenu
                    title="Shared Views Options"
                    tooltip="Shared Views Options"
                    items={this.viewsOptionsActionMenuItems}
                    classes={{
                      menuItem: classes.menuItem,
                      menuButton: classes.actionButton,
                      listItemTypography: classes.listItemOptionsTypography,
                    }}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails classes={{ root: classes.viewsDetails }}>
                <Sortable>
                  <SortableList
                    listId="viewsList"
                    listType="views"
                    classes={{ 'item-container': classes.sortableListItemContainer }}
                    scrollable={false}
                    showDragHandle={false}
                    disableTabbing={true}
                    listRenderer={sortableListRenderer}
                    itemRenderer={this.viewRenderer}
                    items={sidePanelContentsModel.views as IItem[]}
                  />
                </Sortable>
                {!sidePanelContentsModel.hasViews() && (
                  <Typography variant="body2" className={classes.viewsNone}>
                    No Views
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        />
      );
    }
  },
);

const areasStyles = (theme: Theme) => ({
  areaAccordionSummaryContent: {
    height: '24px',
  },
  areaAccordionSecondaryAction: {
    top: '18px',
    left: '30px',
    right: '0px',
    cursor: 'pointer',
  },
  accordionListItem: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  areaLinkContainer: {
    width: '100%',
  },
  areaLink: {
    overflow: 'hidden',
    display: 'block',
    padding: '5px 0px',
    textDecoration: 'none',
    outline: 'none',
    color: theme.palette.navBarPrimary.main,
    '&:active, &:hover, &:focus': {
      textDecoration: 'none',
      outline: 'none',
      // color: theme.palette.common.white,
    },
  },
  addButton: {},
  addButtonInnerContainer: {},
  addButtonIcon: {},
  areaLinkActive: {
    // color: theme.palette.common.white,
  },
  listItemContainer: {},
  listItemLink: {},
  listItemLinkText: {},
  activeLink: {},
  listItemOptions: {},
  listItemOptionsVisible: {},
  listItemOptionsTypography: {},
  menuItem: {},
  actionButton: {},
  sortableListItemContainer: {},
});

interface IAreasProps {
  location: any;
  classes: any;
}

type AreasProps = WithStyle<ReturnType<typeof areasStyles>, IAreasProps>;

const Areas = withStyles<IAreasProps>(
  areasStyles,
  class extends React.Component<AreasProps> {
    constructor(props: AreasProps) {
      super(props);
    }

    shouldComponentUpdate(nextProps: AreasProps) {
      return nextProps.location !== this.props.location;
    }

    handleRenameAreaClick = (area: IArea) => () => {
      sidePanelContentsModel.showRenameAreaDialog(area);
    };

    handleDuplicateAreaClick = (area: IArea) => () => {
      sidePanelContentsModel.showDuplicateAreaDialog(area);
    };

    handleDeleteAreaClick = (areaId: string) => () => {
      sidePanelContentsModel.deleteArea(areaId);
    };

    handleArchiveAreaClick = (areaId: string) => () => {
      sidePanelContentsModel.archiveArea(areaId);
    };

    // handleUnarchiveAreaClick = (areaId: string) => () => {
    //   sidePanelContentsModel.unarchiveArea(areaId);
    // };

    addArea = () => {
      sidePanelContentsModel.showAddAreaDialog();
    };

    areaSettings = (area: IArea) => () => {
      sidePanelContentsModel.showAreaSettingsDialog(area);
    };

    // getAlertsAndNotifications(count: number): JSX.Element {
    //   const title = <Typography variant='body2'>Alerts &amp; Notifications</Typography>;
    //   return <Observe render={() => (
    //     count > 0
    //       ? <Badge badgeContent={count}>{title}</Badge>
    //       : title
    //   )} />;
    // }

    areaRenderer = (area: IArea): JSX.Element => {
      return (
        <Observe
          render={() => {
            const areaExpanded = sidePanelContentsModel.areaPanelIsExpanded(area.id);
            const tabIndex = areaExpanded ? 0 : -1;
            const classes = this.props.classes;
            const optionsActionMenuItems = [
              {
                name: 'renameArea',
                title: 'Rename Area',
                icon: EditIcon,
                onClick: this.handleRenameAreaClick(area),
                closeOnClick: true,
                disabled: () => !mainPageModel.canEditAreas,
              },
              {
                name: 'duplicateArea',
                title: 'Duplicate Area',
                icon: DuplicateIcon,
                onClick: this.handleDuplicateAreaClick(area),
                closeOnClick: true,
                disabled: () => !mainPageModel.canAddAreas,
              },
              {
                name: 'deleteArea',
                title: 'Delete Area',
                icon: DeleteIcon,
                onClick: this.handleDeleteAreaClick(area.id),
                closeOnClick: true,
                disabled: () => !mainPageModel.canDeleteAreas,
              },
              {
                name: 'archiveArea',
                title: 'Archive Area',
                icon: ArchiveIcon,
                onClick: this.handleArchiveAreaClick(area.id),
                closeOnClick: true,
                disabled: () => !mainPageModel.canEditAreas,
              },
              // {name: 'unarchiveArea', title: 'Unarchive Area',  icon: UnarchiveIcon, onClick: this.handleUnarchiveAreaClick(area.id), closeOnClick: true, disabled: () => !mainPageModel.canEditAreas},
            ];

            return (
              <ListItem
                key={area.id}
                className={classes.accordionListItem}
                classes={{ container: classes.listItemContainer }}
                button={false}
              >
                <Accordion
                  onChange={(evt: object, expanded: boolean) => sidePanelContentsModel.setAreaPanelExpanded(area.id, expanded)}
                  defaultExpanded={areaExpanded}
                >
                  <AccordionSummary classes={{ content: classes.areaAccordionSummaryContent }}></AccordionSummary>
                  <AccordionDetails>
                    <Groups
                      area={area}
                      tabIndex={tabIndex}
                      location={window.location}
                      classes={{
                        sortableListItemContainer: classes.sortableListItemContainer,
                        listItemContainer: classes.listItemContainer,
                        listItemLink: classes.listItemLink,
                        listItemLinkText: classes.listItemLinkText,
                        listItemOptions: classes.listItemOptions,
                        listItemOptionsVisible: classes.listItemOptionsVisible,
                        listItemOptionsTypography: classes.listItemOptionsTypography,
                        accordionListItem: classes.accordionListItem,
                        activeLink: classes.activeLink,
                        menuItem: classes.menuItem,
                        actionButton: classes.actionButton,
                        addButton: classes.addButton,
                        addButtonInnerContainer: classes.addButtonInnerContainer,
                        addButtonIcon: classes.addButtonIcon,
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
                <ListItemSecondaryAction className={classes.areaAccordionSecondaryAction}>
                  {
                    // including the following element (and its closing element below) produces the following render error:
                    // Warning: validateDOMNesting(...): <li> cannot appear as a descendant of <li>.
                    // Comment it out does not appear to affect rendering / operation adversely
                    /* <ListItem className={classes.accordionListItem} button={false}> */
                  }
                  <Typography variant="subtitle1" className={classNames(classes.areaLinkContainer, classes.listItemLink)}>
                    <NavLink to={Routes.toArea(area.id)} className={classes.areaLink} activeClassName={classes.areaLinkActive}>
                      {area.name}
                    </NavLink>
                  </Typography>
                  <ListItemSecondaryAction className={classes.listItemOptions}>
                    <OptionsActionMenu
                      title="Area Options"
                      tooltip="Area Options"
                      items={optionsActionMenuItems}
                      classes={{
                        menuItem: classes.menuItem,
                        menuButton: classes.actionButton,
                        listItemTypography: classes.listItemOptionsTypography,
                      }}
                    />
                  </ListItemSecondaryAction>
                  {/* </ListItem> */}
                </ListItemSecondaryAction>
              </ListItem>
            );
          }}
        />
      );
    };

    render() {
      const { classes } = this.props;

      return (
        <Observe
          render={() => (
            <>
              {mainPageModel.canAddAreas && (
                <Button className={classes.addButton} arial-label="Add Area" onClick={this.addArea}>
                  <Divider />
                  <div className={classes.addButtonInnerContainer}>
                    <Typography variant="subtitle1">Add Area</Typography>
                    <AddCircleOutlineIcon className={classes.addButtonIcon} />
                  </div>
                  <Divider />
                </Button>
              )}
              <Sortable>
                <SortableList
                  listId="areasList"
                  listType="areas"
                  classes={{ 'item-container': classes.sortableListItemContainer }}
                  scrollable={false}
                  showDragHandle={false}
                  disableTabbing={true}
                  listRenderer={sortableListRenderer}
                  itemRenderer={this.areaRenderer}
                  items={sidePanelContentsModel.areas as IItem[]}
                />
              </Sortable>
            </>
          )}
        />
      );
    }
  },
);

const groupsStyles = (_theme: Theme) => ({
  addGroupButtonTypography: {
    color: `inherit !important`,
  },
  addButton: {},
  addButtonIcon: {},
  addButtonInnerContainer: {},
  activeLink: {},
  listItemContainer: {
    '&:hover $listItemGroupOptions': {
      visibility: 'visible !important',
      transitionDelay: '35ms',
      transitionProperty: 'visibility',
    },
  },
  listItemLink: {},
  listItemLinkText: {},
  listItemOptions: {},
  listItemGroupOptions: {
    top: 0,
    transform: 'none',
    height: '100%',
    visibility: 'hidden',
  },
  listItemOptionsVisible: {},
  listItemOptionsTypography: {},
  accordionListItem: {},
  menuItem: {},
  actionButton: {},
  sortableListItemContainer: {},
});

interface IGroupsProps {
  area: IArea;
  tabIndex: number;
  location: any;
  classes?: any;
}

type GroupsProps = WithStyle<ReturnType<typeof groupsStyles>, IGroupsProps>;

const Groups = withStyles<IGroupsProps>(
  groupsStyles,
  class extends React.Component<GroupsProps> {
    optionsActionMenuItems: IActionMenuItem[];

    constructor(props: GroupsProps) {
      super(props);
    }

    shouldComponentUpdate(nextProps: GroupsProps) {
      return (
        nextProps.area !== this.props.area ||
        nextProps.tabIndex !== this.props.tabIndex ||
        nextProps.location !== this.props.location
      );
    }

    handleRenameGroupClick = (group: IGroup) => () => {
      sidePanelContentsModel.showRenameGroupDialog(group);
    };

    handleDuplicateGroupClick = (group: IGroup) => () => {
      sidePanelContentsModel.showDuplicateGroupDialog(group);
    };

    handleDeleteGroupClick = (groupId: string) => () => {
      sidePanelContentsModel.deleteGroup(groupId);
    };

    handleArchiveGroupClick = (groupId: string) => () => {
      sidePanelContentsModel.archiveGroup(groupId);
    };

    // handleUnarchiveGroupClick = (groupId: string) => () => {
    //   sidePanelContentsModel.unarchiveGroup(groupId);
    // };

    addGroup = (area: IArea) => () => {
      sidePanelContentsModel.showAddGroupDialog(area);
    };

    isActive = (groupId: string) => (match: any, location: any) => {
      const searchParams = new URLSearchParams(location.search);
      const urlSearchGroupId = searchParams.get('groupId');
      return groupId === urlSearchGroupId;
    };

    groupRenderer = (group: IGroup): JSX.Element => {
      const optionsActionMenuItems = [
        {
          name: 'renameGroup',
          title: 'Rename Group',
          icon: EditIcon,
          onClick: this.handleRenameGroupClick(group),
          closeOnClick: true,
          disabled: () => !mainPageModel.canEditGroups,
        },
        {
          name: 'duplicateGroup',
          title: 'Duplicate Group',
          icon: DuplicateIcon,
          onClick: this.handleDuplicateGroupClick(group),
          closeOnClick: true,
          disabled: () => !mainPageModel.canAddGroups,
        },
        {
          name: 'deleteGroup',
          title: 'Delete Group',
          icon: DeleteIcon,
          onClick: this.handleDeleteGroupClick(group.id),
          closeOnClick: true,
          disabled: () => !mainPageModel.canDeleteGroups,
        },
        {
          name: 'archiveGroup',
          title: 'Archive Group',
          icon: ArchiveIcon,
          onClick: this.handleArchiveGroupClick(group.id),
          closeOnClick: true,
          disabled: () => !mainPageModel.canEditGroups,
        },
        // {name: 'unarchiveGroup', title: 'Unarchive Group', icon: UnarchiveIcon, onClick: this.handleUnarchiveGroupClick(group.id), closeOnClick: true, disabled: () => !mainPageModel.canEditGroups},
      ];

      return (
        <Observe
          render={() => {
            const groupLinkData = mainPageModel.groupMap[group.id].groupLinkData;
            const toUrl = RouteManagerBase.current.createLinkFor({ effective: mainDateRangeNavModel.effective, ...groupLinkData });
            const isActiveFn = this.isActive(group.id);

            return (
              <ListItem
                tabIndex={this.props.tabIndex}
                key={group.id}
                className={this.props.classes.listItemLink}
                classes={{ container: this.props.classes.listItemContainer }}
                component={NavLink}
                to={toUrl}
                activeClassName={this.props.classes.activeLink}
                isActive={isActiveFn}
              >
                <Typography variant="body2" className={this.props.classes.listItemLinkText}>
                  {group.name}
                </Typography>
                <ListItemSecondaryAction
                  className={classNames(
                    this.props.classes.listItemGroupOptions,
                    isActiveFn(undefined, this.props.location) ? this.props.classes.listItemOptionsVisible : undefined,
                  )}
                >
                  <OptionsActionMenu
                    title="Group Options"
                    tooltip="Group Options"
                    items={optionsActionMenuItems}
                    classes={{
                      menuItem: this.props.classes.menuItem,
                      menuButton: this.props.classes.actionButton,
                      listItemTypography: this.props.classes.listItemOptionsTypography,
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            );
          }}
        />
      );
    };

    render() {
      return (
        <Observe
          render={() => {
            const { classes, area, tabIndex } = this.props;
            const a = mainPageModel.areaMap[area.id];
            const groups = a?.groups as IItem[];

            return (
              <>
                <SortableList
                  listId={`groupsList${area.id}`}
                  listType={`groups${area.id}`}
                  classes={{ 'item-container': classes.sortableListItemContainer }}
                  scrollable={false}
                  showDragHandle={false}
                  disableTabbing={true}
                  listRenderer={sortableListRenderer}
                  itemRenderer={this.groupRenderer}
                  items={groups}
                />
                {mainPageModel.canAddGroups && (
                  <Button tabIndex={tabIndex} className={classes.addButton} arial-label="Add Group" onClick={this.addGroup(area)}>
                    <Divider />
                    <div className={classes.addButtonInnerContainer}>
                      <Typography variant="body2" className={classes.addGroupButtonTypography}>
                        Add Group
                      </Typography>
                      <AddCircleOutlineIcon className={classes.addButtonIcon} />
                    </div>
                    <Divider />
                  </Button>
                )}
              </>
            );
          }}
        />
      );
    }
  },
);

const sideToggleStyles = (theme: Theme) => ({
  root: {
    overflowX: 'hidden',
    overflowY: 'auto',
    flex: '1',
    margin: '1px 0px',
    padding: '5px 0px 10px 0px',
  },
  minimized: {
    display: 'none',
    // visibility: 'hidden',
    // '& a[role="button"] > svg': {
    //   visibility: 'hidden !important',
    // },
  },
  listItemContainer: {
    '&:hover $listItemOptions': {
      visibility: 'visible !important',
    },
  },
  listItemLink: {
    paddingRight: '41px',
  },
  listItemLinkText: {
    overflow: 'hidden',
  },
  activeLink: {
    textDecoration: 'none',
    outline: 'none',
    fontStyle: 'italic',
    backgroundColor: theme.palette.primary.dark,
    '&[role="button"] > svg': {
      visibility: 'visible',
    },
    '&:hover, &:active, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  listItemOptions: {
    top: 0,
    transform: 'none',
    height: '100%',
    visibility: 'hidden',
  },
  listItemOptionsVisible: {
    visibility: 'visible !important',
  },
  listItemOptionsTypography: {
    color: 'inherit',
  },
  menuItem: {
    '&:focus': {
      backgroundColor: `${theme.palette.dialogPrimary.main} !important`,
      color: `${theme.palette.dialogPrimary.contrastText} !important`,
      '& > svg': {
        color: `${theme.palette.dialogPrimary.contrastText} !important`,
      },
      '&:hover': {
        backgroundColor: `${theme.palette.dialogPrimary.main} !important`,
      },
    },
  },
  addButton: {
    display: 'block',
    width: '100%',
    textTransform: 'none',
    padding: '3px 8px 3px 30px',
    backgroundColor: 'transparent !important',
    '&:hover, &:active': {
      color: theme.palette.navBarSecondary.light,
      '& hr': {
        backgroundColor: theme.palette.navBarSecondary.light,
      },
    },
  },
  addButtonInnerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addButtonIcon: {
    marginRight: '2px',
  },
  actionButton: {
    height: '100%',
    width: 'auto',
    padding: '0px 10px',
    marginLeft: '0px',
    backgroundColor: 'transparent !important',
    '&:hover, &:active': {
      color: theme.palette.navBarSecondary.light,
    },
  },
  divider: {
    marginLeft: '30px',
  },
  sortableListItemContainer: {
    width: '100%',
  },
});

interface ISidePanelContentsProps {
  open: boolean;
}

type SideTogglePanelProps = WithStyle<ReturnType<typeof sideToggleStyles>, ISidePanelContentsProps & RouteComponentProps<any>>;

export const PlanSidePanelContents = React.memo((props: any) => (
  <>
    <Views
      location={window.location}
      classes={{
        sortableListItemContainer: props.classes.sortableListItemContainer,
        listItemContainer: props.classes.listItemContainer,
        listItemLink: props.classes.listItemLink,
        listItemLinkText: props.classes.listItemLinkText,
        activeLink: props.classes.activeLink,
        menuItem: props.classes.menuItem,
        actionButton: props.classes.actionButton,
        listItemOptions: props.classes.listItemOptions,
        listItemOptionsVisible: props.classes.listItemOptionsVisible,
        listItemOptionsTypography: props.classes.listItemOptionsTypography,
      }}
    />
    <Areas
      location={window.location}
      classes={{
        sortableListItemContainer: props.classes.sortableListItemContainer,
        listItemContainer: props.classes.listItemContainer,
        listItemLink: props.classes.listItemLink,
        listItemLinkText: props.classes.listItemLinkText,
        activeLink: props.classes.activeLink,
        menuItem: props.classes.menuItem,
        actionButton: props.classes.actionButton,
        listItemOptions: props.classes.listItemOptions,
        listItemOptionsVisible: props.classes.listItemOptionsVisible,
        listItemOptionsTypography: props.classes.listItemOptionsTypography,
        addButton: props.classes.addButton,
        addButtonInnerContainer: props.classes.addButtonInnerContainer,
        addButtonIcon: props.classes.addButtonIcon,
      }}
    />
    <PlanSidePanelContentsDialogs />
  </>
));

export const PlanSidePanelContentsDialogs = React.memo((_props: any) => (
  <>
    <AdvancedSearchDialog />
    <AddAreaDialog />
    <AddGroupDialog />
    <RenameAreaDialog />
    <RenameGroupDialog />
    <DuplicateAreaDialog />
    <DuplicateGroupDialog />
    <ArchiveDeleteAreaDialog />
    <ArchiveDeleteGroupDialog />
    <SearchViewDialog />
    {/* <AreaSettingsDialog /> */}
  </>
));

class SidePanelContents extends React.PureComponent<SideTogglePanelProps> {
  constructor(props: SideTogglePanelProps) {
    super(props);

    sidePanelContentsModel.load();
  }

  componentWillUnmount() {
    sidePanelContentsModel.reset();
  }

  render() {
    const { open, ...restProps } = this.props;
    const { classes } = restProps;

    return (
      <Observe
        render={() => (
          <div className={classNames(classes.root, open ? '' : classes.minimized)}>
            {/* <Favourites location={window.location} classes={{sortableListItemContainer: classes.sortableListItemContainer, listItemLink: classes.listItemLink, listItemLinkText: classes.listItemLinkText, activeLink: classes.activeLink, actionButton: classes.actionButton}} /> */}
            <Observe
              render={() =>
                (sideTogglePanelModel.SidePanelContent && <sideTogglePanelModel.SidePanelContent {...restProps} />) || (
                  <PlanSidePanelContents {...restProps} />
                )
              }
            />
          </div>
        )}
      />
    );
  }
}

export default withStyles<ISidePanelContentsProps>(sideToggleStyles, SidePanelContents);
